/**
 * ThemeContext
 * 
 * Unified theme context that combines:
 * - Theme mode (light/dark)
 * - Theme colors (default/sepia/dracula)
 * - Color scheme (accent colors)
 * - Text color overrides
 * - CSS variable management
 */

import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useThemeMode, ThemeMode } from '../hooks/useThemeMode';
import { useThemeColors, ThemeName } from '../hooks/useThemeColors';
import { useColorScheme, ColorSchemeName, ColorScheme } from '../hooks/useColorScheme';
import { useTextColorOverrides } from '../hooks/useTextColorOverrides';
import { useThemeCSSVariables } from '../hooks/useThemeCSSVariables';
import { ThemeColors } from '../hooks/useThemeColors';

export interface ThemeContextValue {
  // Mode
  mode: ThemeMode;
  isDarkMode: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  
  // Theme
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  themeColors: Record<ThemeName, Record<ThemeMode, ThemeColors>>;
  currentColors: ThemeColors;
  
  // Color Scheme
  colorScheme: ColorSchemeName;
  setColorScheme: (scheme: ColorSchemeName) => void;
  setCustomColor: (color: string) => void;
  colorSchemes: Record<ColorSchemeName, ColorScheme>;
  currentScheme: ColorScheme;
  
  // Text Color Overrides
  titleColorOverride: string | null;
  bodyColorOverride: string | null;
  setTitleColor: (color: string) => void;
  setBodyTextColor: (color: string) => void;
  clearTextColorOverrides: () => void;
  
  // Mounted state
  mounted: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  
  // Initialize all sub-hooks
  const modeHook = useThemeMode();
  const colorHook = useThemeColors(modeHook.mode);
  const schemeHook = useColorScheme();
  const overridesHook = useTextColorOverrides();
  
  // Apply CSS variables whenever relevant values change
  useThemeCSSVariables({
    colors: colorHook.currentColors,
    titleColorOverride: overridesHook.titleColorOverride,
    bodyColorOverride: overridesHook.bodyColorOverride,
    mounted
  });
  
  // Set mounted after all hooks have initialized
  useEffect(() => {
    // Small delay to ensure all hooks have loaded their data
    const timer = setTimeout(() => {
      setMounted(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);
  
  const value: ThemeContextValue = {
    // Mode
    mode: modeHook.mode,
    isDarkMode: modeHook.isDarkMode,
    setMode: modeHook.setMode,
    toggleMode: modeHook.toggleMode,
    
    // Theme
    theme: colorHook.theme,
    setTheme: colorHook.setTheme,
    themeColors: colorHook.themeColors,
    currentColors: colorHook.currentColors,
    
    // Color Scheme
    colorScheme: schemeHook.colorScheme,
    setColorScheme: schemeHook.setColorScheme,
    setCustomColor: schemeHook.setCustomColor,
    colorSchemes: schemeHook.colorSchemes,
    currentScheme: schemeHook.currentScheme,
    
    // Text Color Overrides
    titleColorOverride: overridesHook.titleColorOverride,
    bodyColorOverride: overridesHook.bodyColorOverride,
    setTitleColor: overridesHook.setTitleColor,
    setBodyTextColor: overridesHook.setBodyTextColor,
    clearTextColorOverrides: overridesHook.clearTextColorOverrides,
    
    // Mounted
    mounted
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

